import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Eye, EyeOff, Shield, Mail, Lock } from 'lucide-react';
import MFAVerification from './MFAVerification';

const LoginPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [mfaStep, setMfaStep] = useState<{ sessionToken: string } | null>(null);
  const [emailFocused, setEmailFocused] = useState(false);
  const [passwordFocused, setPasswordFocused] = useState(false);
  
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      const result = await login(email, password);
      if (result === true) {
        navigate('/dashboard');
      } else if (typeof result === 'object' && result.requiresMFA) {
        setMfaStep({ sessionToken: result.sessionToken });
      } else {
        setError('Identifiants invalides');
      }
    } catch (err) {
      setError((err as Error).message || 'Erreur de connexion');
    } finally {
      setIsLoading(false);
    }
  };

  const handleMFASuccess = (tokens: { accessToken: string; refreshToken: string; user: any }) => {
    console.log('🔍 handleMFASuccess called with tokens:', tokens);
    
    // Stocker les tokens dans localStorage
    localStorage.setItem('accessToken', tokens.accessToken);
    localStorage.setItem('refreshToken', tokens.refreshToken);
    
    console.log('✅ Tokens stored, reloading page to trigger AuthContext');
    
    // Forcer le rechargement complet pour déclencher AuthContext
    window.location.href = '/dashboard';
  };

  const handleMFABack = () => {
    setMfaStep(null);
    setError('');
  };

  // Si MFA est requis, afficher le composant MFAVerification
  if (mfaStep) {
    return (
      <MFAVerification
        sessionToken={mfaStep.sessionToken}
        onSuccess={handleMFASuccess}
        onBack={handleMFABack}
      />
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-osint-dark p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-96 h-48 bg-transparent mb-4">
            <img 
              src="/logo.png"
              alt="Logo" 
              className="w-full h-full object-contain"
            />
          </div>
        </div>

        {/* Formulaire de connexion */}
        <div className="osint-card rounded-2xl p-8">
          <div className="flex items-center mb-6">
            <Shield className="w-6 h-6 text-osint-orange mr-3" />
            <h2 className="text-xl font-semibold text-white">Connexion Sécurisée</h2>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Email avec icône et label flottant */}
            <div className="relative">
              <div className="relative">
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onFocus={() => setEmailFocused(true)}
                  onBlur={() => setEmailFocused(false)}
                  className="osint-input w-full pl-12 pr-4 py-4 rounded-xl text-white"
                  placeholder=" "
                  required
                />
                <Mail className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <label 
                  htmlFor="email" 
                  className={`absolute left-12 top-1/2 transform -translate-y-1/2 text-gray-400 transition-all duration-300 pointer-events-none ${
                    email || emailFocused 
                      ? 'top-2 left-4 text-xs text-orange-500 bg-gray-800 px-2 py-1 rounded' 
                      : ''
                  }`}
                >
                  Adresse Email
                </label>
              </div>
            </div>

            {/* Mot de passe avec toggle visibilité */}
            <div className="relative">
              <div className="relative">
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onFocus={() => setPasswordFocused(true)}
                  onBlur={() => setPasswordFocused(false)}
                  className="osint-input w-full pl-12 pr-12 py-4 rounded-xl text-white"
                  placeholder=" "
                  required
                />
                <Lock className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <label 
                  htmlFor="password" 
                  className={`absolute left-12 top-1/2 transform -translate-y-1/2 text-gray-400 transition-all duration-300 pointer-events-none ${
                    password || passwordFocused 
                      ? 'top-2 left-4 text-xs text-orange-500 bg-gray-800 px-2 py-1 rounded' 
                      : ''
                  }`}
                >
                  Mot de passe
                </label>
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-orange-500 transition-colors z-20 p-2 rounded-lg hover:bg-orange-500/10"
                  aria-label={showPassword ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {/* Message d'erreur */}
            {error && (
              <div className="bg-red-900/20 border border-red-500/50 rounded-lg p-3 text-red-400 text-sm animate-fade-in">
                {error}
              </div>
            )}

            {/* Bouton de connexion */}
            <button
              type="submit"
              disabled={isLoading}
              className="osint-button w-full py-4 px-6 rounded-xl text-white font-semibold disabled:opacity-50 disabled:cursor-not-allowed text-lg transition-all duration-300 hover:scale-105"
            >
              {isLoading ? (
                <div className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-3"></div>
                  Connexion...
                </div>
              ) : (
                'Se connecter'
              )}
            </button>
          </form>

          {/* Informations supplémentaires */}
          <div className="mt-6 text-center">
            <p className="text-xs text-gray-400">
              Accès réservé aux détectives privés autorisés
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center mt-8">
          <p className="text-sm text-gray-500">
            Système de sécurité OSINT v1.0
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;